// CHAT-PRESENCE (2026-10-29): closing DMs in the viewer's own list. The hover × closes one
// (`dms.close`; history kept); "Clean up" in the section's ⋯ closes those quiet for 7, 14 or 30
// days (`dms.cleanup`), never one where a DE needs the viewer. A closed DM opens again when
// either side writes (the server reads it), when the viewer opens it from Team (`dms.open`), or
// when the viewer lands on it any other way — ⌘K, a link — which reopens it here.
import { DM_CLEANUP_DAYS, dmQuiet } from "@companyos/domain-work/chat";
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  toast,
} from "@companyos/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { type FormEvent, useEffect, useRef, useState } from "react";
import { keepFocus } from "../../focus/focus";
import { problemText } from "../ops-parts";
import { dmMemberIds } from "./chat-model";
import { chatKeys, type SidebarChannel } from "./chat-queries";
import type { DePresence } from "./de-presence";
import type { ChatContext } from "./use-chat";

/** The × on a DM row: closes it for the viewer; leaves it when it was the open one. */
export function useCloseDm(chat: ChatContext, channel: SidebarChannel, active: boolean) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: () => chat.api.client.dms.close({ params: { co: chat.co, id: channel.id } }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: chatKeys.channels(chat.co) });
      // Off the closed DM to Chat's landing (which no longer lists it).
      if (active)
        await navigate({
          to: "/c/$slug/chat",
          params: { slug: chat.company.slug },
          search: keepFocus as never,
        });
    },
    onError: (e) =>
      toast.add({
        title: "Could not close the conversation",
        description: problemText(e, e.message),
        type: "error",
      }),
  });
}

/**
 * Arriving at a DM the viewer closed (⌘K, a link, a notification) opens it again, as Team does:
 * the open conversation is listed. Only the arrival decides — a DM closed while the viewer is on
 * it (the ×, "Clean up", another tab) stays closed.
 */
export function useReopenOnVisit(
  chat: ChatContext,
  channels: readonly SidebarChannel[],
  channelId: string | undefined,
) {
  const queryClient = useQueryClient();
  // The conversation just arrived at, until the channel list has been read for it.
  const arrived = useRef<string | undefined>(channelId);
  const seen = useRef<string | undefined>(channelId);
  if (seen.current !== channelId) {
    seen.current = channelId;
    arrived.current = channelId;
  }
  const row = channelId ? channels.find((c) => c.id === channelId) : undefined;
  const can = chat.meId !== null && !chat.readOnly;
  useEffect(() => {
    if (!row || arrived.current !== row.id) return;
    arrived.current = undefined; // decided once per arrival
    if (!row.closed || !can) return;
    const others = dmMemberIds(row).filter((id) => id !== chat.meId);
    if (others.length === 0) return;
    void chat.api.client.dms
      .open({ params: { co: chat.co }, body: { membership_ids: others } })
      .then(() => queryClient.invalidateQueries({ queryKey: chatKeys.channels(chat.co) }))
      .catch(() => {
        // It stays closed in the list; the conversation itself still shows.
      });
  }, [row, can, chat.api, chat.co, chat.meId, queryClient]);
}

/** The DMs "Clean up" would close for `days` (the server decides; this is the preview). */
export function cleanupCandidates(
  dms: readonly SidebarChannel[],
  meId: string | null,
  presence: ReadonlyMap<string, DePresence>,
  days: number,
  now = new Date(),
): SidebarChannel[] {
  return dms.filter((c) => {
    if (c.closed || !dmQuiet(c.last_message_at, c.created_at, days, now)) return false;
    return !dmMemberIds(c).some((id) => id !== meId && presence.get(id)?.state === "needs_you");
  });
}

/** "Clean up" (a modal): pick how quiet, see how many, close them. */
export function DmCleanupDialog({
  chat,
  dms,
  presence,
  onClose,
}: {
  chat: ChatContext;
  /** The viewer's open DMs (listed or not). */
  dms: readonly SidebarChannel[];
  presence: ReadonlyMap<string, DePresence>;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [days, setDays] = useState<(typeof DM_CLEANUP_DAYS)[number]>(14);
  const count = cleanupCandidates(dms, chat.meId, presence, days).length;
  const run = useMutation({
    mutationFn: () =>
      chat.api.client.dms.cleanup({ params: { co: chat.co }, body: { quiet_days: days } }),
    onSuccess: async (r) => {
      await queryClient.invalidateQueries({ queryKey: chatKeys.channels(chat.co) });
      const n = r.closed_channel_ids.length;
      toast.add({
        title: n ? `Closed ${n} ${n === 1 ? "conversation" : "conversations"}` : "Nothing to close",
        type: "success",
      });
      onClose();
    },
    onError: (e) =>
      toast.add({
        title: "Could not clean up",
        description: problemText(e, e.message),
        type: "error",
      }),
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    run.mutate();
  };
  return (
    <Dialog open onOpenChange={(o) => !o && !run.isPending && onClose()}>
      <DialogContent data-slot="dm-cleanup">
        <form onSubmit={submit} className="flex flex-col gap-5">
          <DialogHeader>
            <DialogTitle>Clean up direct messages</DialogTitle>
            <DialogDescription>
              Close quiet conversations. History is kept; a new message brings one back.
            </DialogDescription>
          </DialogHeader>
          <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
            <legend className="mb-2 text-sm font-medium">Quiet for</legend>
            <div className="flex gap-2">
              {DM_CLEANUP_DAYS.map((d) => (
                <label
                  key={d}
                  className="flex cursor-pointer items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm has-checked:border-primary has-checked:bg-primary-soft"
                >
                  <input
                    type="radio"
                    name="quiet-days"
                    value={d}
                    checked={days === d}
                    onChange={() => setDays(d)}
                    className="accent-primary"
                  />
                  {d} days
                </label>
              ))}
            </div>
            <p className="text-xs text-muted-foreground" data-slot="dm-cleanup-count">
              {count === 0
                ? "No conversation is that quiet."
                : `${count} ${count === 1 ? "conversation" : "conversations"} will close.`}{" "}
              Never one where a DE needs you.
            </p>
          </fieldset>
          <DialogFooter>
            <DialogClose render={<Button variant="ghost" type="button" />}>Cancel</DialogClose>
            <Button type="submit" disabled={run.isPending || count === 0}>
              Close {count || ""}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
